import { useLayoutEffect, useRef, useState } from "react";

/**
 * Hook to track the width of a DOM element using ResizeObserver.
 * Returns a ref to attach to the element and the current width.
 *
 * @returns Object containing the ref and the current width in pixels (0 if not yet measured)
 *
 * @example
 * const { ref, width } = useContainerWidth();
 * return <div ref={ref}>{width > 600 ? "Wide" : "Narrow"}</div>
 */
export const useContainerWidth = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = Math.round(entry.contentRect.width);
      setWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    });

    setWidth(Math.round(element.getBoundingClientRect().width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
};
